import React, { useState } from "react";
import { AdminSidebar } from "../Sidebar/Sidebar";
import { AdminTopBar } from "../topbar/Topbar";
import "../Quest/Quests.css";
import "../toggle.css";
import { useParams } from "react-router-dom";
import BackButton from "../Goback/GoBack";
import classroomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import TopImage from "../../common/TopImage";
import html2pdf from "html2pdf.js";
import { useGetInvoice } from "../../../../hooks/admin/InvoicePayments/useGetInvoice";
import { InvoiceSkeleton } from "./InvoiceSkeleton";

export const AdminAgreementViewInvoice = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  const { invoiceId } = useParams();
  const { invoice, loading, error } = useGetInvoice(invoiceId);

  // format bank_transfer → Bank Transfer
  const formatMethod = (method) =>
    method?.replace(/_/g, " ")?.replace(/\b\w/g, (c) => c.toUpperCase()) ||
    "--";

  // format date
  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "--";

  const handleDownloadPDF = async () => {
    const element = document.getElementById("invoice-pdf");
    if (!element) return;

    setIsDownloading(true);

    const options = {
      margin: 10,
      filename: `invoice-${invoice?.invoice_no || invoiceId}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    try {
      await html2pdf().set(options).from(element).save();
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (error) {
    return <div className="text-center text-danger mt-5">{error}</div>;
  }

  return (
    <div className="dashboardBody">
      <AdminSidebar isSidebarCollapsed={isSidebarCollapsed} />
      <div className="container-fluid p-0">
        <div id="mainContent">
          <AdminTopBar toggleSidebar={toggleSidebar} />
          <div className="teacher-bodycontent mb-4">
            <section>
              <div className="container-fluid">
                <div className="row mb-4 align-items-center">
                  <div className="col-12">
                    <BackButton />
                  </div>

                  <TopImage imageSrc={classroomImg} text="INVOICE" />

                  <div className="col-md-12 text-md-end my-4">
                    <button
                      onClick={handleDownloadPDF}
                      className="add-school-btn"
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <i
                            className="fa fa-download me-2"
                            aria-hidden="true"
                          />
                          Download PDF
                        </>
                      )}
                    </button>
                  </div>

                  {/* ================= EXACT INVOICE VIEW ================= */}
                  <div className="row justify-content-center mb-5">
                    <div className="col-lg-12">
                      {loading && <InvoiceSkeleton />}

                      {!loading && !invoice && (
                        <div className="text-center text-muted py-5">
                          No invoice found
                        </div>
                      )}
                      {!loading && invoice && (
                        <div
                          className="card border-0 shadow-sm p-4"
                          id="invoice-pdf"
                        >
                          {/* Logo */}
                          <div className="text-center mb-3">
                            <h4 className="fw-bold text-primary mb-0">
                              YQ <span className="text-dark">YUVA QUEST</span>
                            </h4>
                          </div>

                          <hr />

                          {/* Top Info */}
                          <div className="row mt-3 mb-4">
                            <div className="col-md-6">
                              <div className="d-flex mb-2">
                                <strong className="me-2">School Name</strong>:{" "}
                                {invoice?.institution?.name || "N/A"}
                              </div>

                              <div className="d-flex mb-2">
                                <strong className="me-2">Email</strong>:{" "}
                                {invoice?.institution?.email ||
                                  "admin@school.com"}
                              </div>

                              <div className="d-flex">
                                <strong className="me-2">Contact</strong>:{" "}
                                {invoice?.institution?.phone || "+91-XXXX-XXXX"}
                              </div>
                            </div>

                            <div className="col-md-6 text-md-end mt-3 mt-md-0">
                              <div className="d-flex justify-content-md-end mb-2">
                                <strong className="me-2">Invoice No</strong> :
                                {invoice?.invoice_no}
                              </div>

                              <div className="d-flex justify-content-md-end">
                                <strong className="me-2">Date</strong> :
                                {formatDate(invoice.issued_at)}
                              </div>

                              <div className="d-flex justify-content-md-end">
                                <strong className="me-2">Due Date</strong> :
                                {formatDate(invoice?.due_at)}
                              </div>
                              {invoice.status === "paid" ? (
                                <span className="badge bg-success mt-2">
                                  Paid
                                </span>
                              ) : invoice.status === "cancelled" ? (
                                <span className="badge bg-danger mt-2">
                                  Cancelled
                                </span>
                              ) : (
                                <span className="badge bg-secondary mt-2">
                                  {invoice.status}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Invoice Summary */}
                          <h6 className="fw-bold mt-4 mb-3">Invoice Summary</h6>

                          <div className="table-responsive">
                            <table className="table table-borderless">
                              <thead className="border-bottom">
                                <tr className="text-muted small">
                                  <th>DESCRIPTION</th>
                                  <th className="text-center">QTY</th>
                                  <th className="text-end">UNIT PRICE (INR)</th>
                                  <th className="text-end">TOTAL ({invoice?.currency})</th>
                                </tr>
                              </thead>

                              <tbody>
                                {invoice.cycle && (
                                  <tr className="border-bottom">
                                    <td>
                                      Student Licenses (Cycle{" "}
                                      {invoice?.cycle?.cycle_year})
                                    </td>
                                    <td className="text-center">
                                      {invoice?.cycle?.base_licenses}
                                    </td>
                                    <td className="text-end">
                                      {(
                                        invoice.total_amount /
                                        invoice?.cycle?.base_licenses
                                      ).toFixed(2)}
                                    </td>
                                    <td className="text-end">
                                      {invoice?.total_amount}
                                    </td>
                                  </tr>
                                )}

                                <tr>
                                  <td
                                    colSpan="3"
                                    className="text-end text-muted"
                                  >
                                    Subtotal
                                  </td>
                                  <td className="text-end">
                                    {Number(
                                      invoice?.total_amount,
                                    ).toLocaleString("en-IN")}
                                  </td>
                                </tr>

                                <tr>
                                  <td
                                    colSpan="3"
                                    className="text-end text-muted"
                                  >
                                    Paid Amount
                                  </td>
                                  <td className="text-end">
                                    {Number(invoice?.paid_amount).toLocaleString(
                                      "en-IN",
                                    )}
                                  </td>
                                </tr>

                                <tr className="border-top fw-bold">
                                  <td colSpan="3" className="text-end">
                                    Balance
                                  </td>
                                  <td className="text-end">
                                    {Number(
                                      invoice?.total_amount -
                                        invoice?.paid_amount,
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Payments */}
                          {invoice?.payments?.length > 0 && (
                            <>
                              <h6 className="fw-bold mt-4 mb-2">Payments</h6>
                              <div className="table-responsive">
                                <table className="table table-borderless">
                                  <thead className="border-bottom">
                                    <tr className="text-muted small">
                                      <th>Amount (INR)</th>
                                      <th className="text-center">Method</th>
                                      <th className="text-center">Reference</th>
                                      <th className="text-end">Processed At</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoice.payments?.map((p) => (
                                      <tr key={p.id}>
                                        <td>
                                          {Number(p.amount).toLocaleString(
                                            "en-IN",
                                          )}
                                        </td>
                                        <td className="text-center">
                                          {formatMethod(p.method)}
                                        </td>
                                        <td className="text-center">
                                          {p.reference_id || "--"}
                                        </td>
                                        <td className="text-end">
                                          {formatDate(p.processed_at)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}

                          {/* Payment Instructions */}
                          <h6 className="fw-bold mt-4 mb-2">
                            Payment Instructions
                          </h6>
                          <ol className="small text-muted ps-3">
                            <li className="mb-2">
                              Online Payment: You’ll receive a secure Razorpay
                              link. Pay online within the validity period.
                            </li>
                            <li>
                              Manual Payment: Transfer the total amount to Yuva
                              Quest’s official bank account and share the
                              payment receipt/screenshot with our team.
                            </li>
                          </ol>

                          <p className="small text-muted mt-3 mb-0">
                            For assistance: support@yuvaquest.in | +91 91234
                            56789
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* ================= END ================= */}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
